import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, FileText, HelpCircle, Video, Download, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcon = {
  Article: FileText,
  FAQ: HelpCircle,
  Video: Video,
  Download: Download,
};

const ResourceDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [resource, setResource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/resources/${slug}`)
      .then((res) => setResource(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="pt-32 pb-24 bg-white min-h-screen">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-slate-500">Loading…</div>
      </div>
    );
  }

  if (error || !resource) {
    return (
      <div className="pt-32 pb-24 bg-white min-h-screen">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <h1 className="font-serif text-3xl font-bold text-slate-900 mb-4">Resource Not Found</h1>
          <button
            onClick={() => navigate("/resources")}
            className="text-[#C89B3C] font-medium hover:underline"
          >
            ← Back to Resources
          </button>
        </div>
      </div>
    );
  }

  const Icon = typeIcon[resource.type] || FileText;

  return (
    <div className="pt-24 pb-24 bg-white min-h-screen">
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        <Link
          to="/resources"
          className="inline-flex items-center gap-2 text-[#C89B3C] hover:text-slate-900 text-sm font-medium mb-8 transition-colors"
          data-testid="resource-back"
        >
          <ArrowLeft size={16} /> Back to Resources
        </Link>

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-slate-500">
              {resource.category}
            </span>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1 text-xs text-slate-500">
              <Icon size={14} className="text-[#C89B3C]" />
              {resource.type}
            </span>
          </div>

          <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#0B2343] leading-tight mb-6">
            {resource.title}
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mb-10">{resource.excerpt}</p>

          {resource.externalUrl && (
            <a
              href={resource.externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#0B2343] text-white px-6 py-3 font-medium hover:bg-slate-800 transition-colors mb-10"
              data-testid="resource-external"
            >
              Open Link <ExternalLink size={16} />
            </a>
          )}

          {resource.content && (
            <div
              className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-line"
              data-testid="resource-content"
            >
              {resource.content}
            </div>
          )}

          <div className="mt-16 pt-8 border-t border-slate-200 text-sm text-slate-500">
            Published {new Date(resource.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
          </div>
        </motion.article>
      </div>
    </div>
  );
};

export default ResourceDetailPage;
